import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Calendar, FileText, Video, Image as ImageIcon,
  BookOpen, PlayCircle, Loader2, Tag, Download,
} from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import { api } from '../lib/api';

const TYPE_META = {
  article: { icon: FileText, label: 'Article', tone: 'from-violet-500 to-fuchsia-500' },
  video:   { icon: Video,    label: 'Video',   tone: 'from-rose-500 to-pink-500' },
  image:   { icon: ImageIcon, label: 'Image',  tone: 'from-amber-500 to-orange-500' },
  guide:   { icon: BookOpen, label: 'User Guide', tone: 'from-emerald-500 to-teal-500' },
};

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'guide', label: 'User Guides' },
  { value: 'video', label: 'Videos' },
  { value: 'article', label: 'Articles' },
  { value: 'image', label: 'Images' },
];

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/youtube\.com\/embed\/([\w-]{11})/);
  if (m) return url;
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

export function ResourcesIndex() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    api.get('/api/public/resources')
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (tab === 'all') return items;
    return items.filter((r) => r.type === tab);
  }, [items, tab]);

  return (
    <>
      <PageHero
        eyebrow="Knowledge"
        title="Resources & User Guides"
        subtitle="Step-by-step guides, video tutorials and downloadable assets to help you get the most from VoIPinger."
        breadcrumb="Resources"
      />

      <section className="bg-white">
        <div className="container-x section-pad">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                data-testid={`resource-tab-${t.value}`}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  tab === t.value
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-fuchsia-500/30'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {items === null ? (
            <div className="py-16 text-center text-slate-500">
              <Loader2 size={20} className="animate-spin inline mr-2" /> Loading resources…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <BookOpen size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No resources in this section yet — check back soon.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((r) => <ResourceCard key={r.slug} r={r} />)}
            </div>
          )}
        </div>
      </section>

      <CtaBanner title="Need a hand?" subtitle="Talk to a VoIPinger specialist — most questions are answered within minutes." primary="Contact support" />
    </>
  );
}

function ResourceCard({ r }) {
  const meta = TYPE_META[r.type] || TYPE_META.article;
  const Ic = meta.icon;
  const isExternal = r.type === 'image' || (r.type === 'video' && !getEmbedUrl(r.media_url));
  const linkProps = isExternal
    ? { href: r.media_url || '#', target: '_blank', rel: 'noreferrer' }
    : { to: `/resources/${r.slug}` };
  const Wrap = isExternal ? 'a' : Link;

  return (
    <Wrap {...linkProps} className="card-modern p-0 overflow-hidden group block" data-testid={`resource-card-${r.slug}`}>
      <div className="aspect-[16/10] overflow-hidden bg-slate-100 relative">
        {r.cover_image ? (
          <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        ) : r.type === 'image' && r.media_url ? (
          <img src={r.media_url} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${meta.tone} flex items-center justify-center`}>
            <Ic size={40} className="text-white/80" />
          </div>
        )}
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${meta.tone} shadow-lg`}>
          <Ic size={11} /> {meta.label}
        </span>
        {r.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition">
            <PlayCircle size={48} className="text-white drop-shadow-2xl" />
          </div>
        )}
      </div>
      <div className="p-5">
        {r.duration && <p className="text-xs text-slate-500 mb-1">{r.duration}</p>}
        <h3 className="font-bold text-slate-900 mb-2 leading-snug group-hover:text-violet-700 transition-colors line-clamp-2">{r.title}</h3>
        {r.excerpt && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{r.excerpt}</p>}
        <span className="text-violet-600 font-semibold text-sm flex items-center gap-1">
          {r.type === 'image' ? <>Download <Download size={14} /></> : r.type === 'video' ? <>Watch <PlayCircle size={14} /></> : <>Read more <ArrowRight size={14} /></>}
        </span>
      </div>
    </Wrap>
  );
}

export function ResourceDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null); setR(null);
    api.get(`/api/public/resources/${slug}`)
      .then((res) => setR(res.data))
      .catch(() => setError('not_found'))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [slug]);

  useEffect(() => {
    if (!r) return;
    document.title = `${r.title} — VoIPinger Resources`;
  }, [r]);

  if (loading) return <div className="py-32 text-center text-slate-500"><Loader2 size={20} className="animate-spin inline mr-2" /> Loading…</div>;
  if (error || !r) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <BookOpen size={40} className="text-violet-400 mb-3" />
        <h1 className="text-2xl font-extrabold mb-2">Resource not found</h1>
        <Link to="/resources" className="btn-primary mt-4"><ArrowLeft size={16} /> Back to Resources</Link>
      </div>
    );
  }

  const meta = TYPE_META[r.type] || TYPE_META.article;
  const embed = r.type === 'video' ? getEmbedUrl(r.media_url) : null;

  return (
    <article className="bg-white">
      <div className="container-x pt-32 pb-6">
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-700 hover:text-violet-900 text-sm font-semibold mb-6 transition-all"
        >
          <ArrowLeft size={14} /> Back to all resources
        </Link>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${meta.tone}`}>
          <meta.icon size={12} /> {meta.label}
        </span>
        <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mt-4 max-w-4xl">{r.title}</h1>
        {r.excerpt && <p className="text-lg text-slate-600 mt-4 max-w-3xl leading-relaxed">{r.excerpt}</p>}
        <div className="flex items-center gap-5 mt-4 text-slate-500 text-sm">
          {r.duration && <span className="flex items-center gap-2"><Calendar size={14} /> {r.duration}</span>}
          {(r.tags || []).length > 0 && <span className="flex items-center gap-2"><Tag size={14} /> {r.tags.join(', ')}</span>}
        </div>

        {/* PDF / downloadable asset — give the visitor a direct download
            CTA at the top of the page. Anything that isn't a video, image
            or article uses this; PDFs (guides) are the main use case. */}
        {r.media_url && r.type !== 'image' && r.type !== 'video' && r.type !== 'article' && (
          <a
            href={r.media_url}
            target="_blank"
            rel="noreferrer"
            download
            className="btn-primary mt-6 inline-flex"
          >
            <Download size={16} /> Download PDF
          </a>
        )}
      </div>

      <div className="container-x py-8 md:py-12">
        {embed ? (
          <div className="aspect-video rounded-2xl overflow-hidden shadow-xl max-w-4xl mb-10">
            <iframe src={embed} title={r.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        ) : r.type === 'image' && r.media_url ? (
          <img src={r.media_url} alt={r.title} className="rounded-2xl shadow-xl max-w-4xl mb-10" />
        ) : null}

        <div className="blog-content max-w-3xl text-slate-700 text-[17px] leading-[1.8]" dangerouslySetInnerHTML={{ __html: r.content || '' }} />
      </div>

      <CtaBanner title="Ready to try VoIPinger?" subtitle="Start a 14-day free trial — no credit card, no long contracts." primary="Start free trial" />
    </article>
  );
}
